import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CategoryShowcase = ({ onCategoryClick }) => {
  const categories = [
    {
      id: 'photography',
      name: 'Wedding Photography',
      cultural: 'Capture Your Sacred Moments',
      description: 'Professional photographers specializing in South Asian wedding traditions',
      icon: 'Camera',
      image: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      vendorCount: 245,
      startingPrice: 2500,
      gradient: 'from-primary-500 to-primary-600'
    },
    {
      id: 'mehndi',
      name: 'Mehndi Artists',
      cultural: 'Traditional Henna Artistry',
      description: 'Skilled artists creating intricate bridal and guest mehndi designs',
      icon: 'Palette',
      image: 'https://images.pexels.com/photos/1721932/pexels-photo-1721932.jpeg?auto=compress&cs=tinysrgb&w=800&q=80',
      vendorCount: 128,
      startingPrice: 150,
      gradient: 'from-secondary-500 to-secondary-600'
    },
    {
      id: 'mandap',
      name: 'Mandap Decorators',
      cultural: 'Sacred Canopy Design',
      description: 'Beautiful mandap setups for your ceremony with traditional elements',
      icon: 'Home',
      image: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      vendorCount: 89,
      startingPrice: 1200,
      gradient: 'from-accent-500 to-accent-600'
    },
    {
      id: 'music',
      name: 'DJs & Musicians',
      cultural: 'Bollywood & Traditional Music',
      description: 'DJs and live musicians for sangeet, ceremony, and reception',
      icon: 'Music',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      vendorCount: 156,
      startingPrice: 800,
      gradient: 'from-primary-600 to-secondary-500'
    },
    {
      id: 'catering',
      name: 'Catering Services',
      cultural: 'Authentic Indian Cuisine',
      description: 'Traditional and fusion catering for all wedding events',
      icon: 'ChefHat',
      image: 'https://images.pexels.com/photos/958545/pexels-photo-958545.jpeg?auto=compress&cs=tinysrgb&w=800&q=80',
      vendorCount: 203,
      startingPrice: 45,
      gradient: 'from-success-500 to-success-600'
    },
    {
      id: 'makeup',
      name: 'Makeup Artists',
      cultural: 'Bridal Beauty Specialists',
      description: 'Professional makeup artists experienced in South Asian bridal looks',
      icon: 'Sparkles',
      image: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      vendorCount: 167,
      startingPrice: 300,
      gradient: 'from-warning-500 to-warning-600'
    }
  ];

  const handleCategoryClick = (categoryId) => {
    onCategoryClick?.(categoryId);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-surface-50 to-surface-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4">
            Explore Wedding Categories
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto font-body">
            Discover specialized vendors for every aspect of your South Asian wedding celebration.
            From traditional ceremonies to modern receptions, find experts who understand your cultural needs.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className="group relative bg-white rounded-xl shadow-soft border border-border hover:shadow-strong transition-all duration-300 cursor-pointer overflow-hidden"
            >
              {/* Background Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${category.gradient} opacity-60 group-hover:opacity-70 transition-opacity`} />

                {/* Category Icon */}
                <div className="absolute top-4 left-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <Icon name={category.icon} size={24} color="#FFFFFF" />
                </div>

                {/* Vendor Count Badge */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-sm font-medium text-text-primary">
                    {category.vendorCount}+ vendors
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-heading font-bold text-xl text-text-primary mb-2 group-hover:text-primary transition-colors">
                  {category.name}
                </h3>
                <p className="text-sm font-medium text-primary mb-2">
                  {category.cultural}
                </p>
                <p className="text-text-secondary font-caption mb-4 line-clamp-2">
                  {category.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-text-secondary">
                    Starting at <span className="font-bold text-text-primary">${category.startingPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-primary group-hover:translate-x-1 transition-transform">
                    <span className="text-sm font-medium">Explore</span>
                    <Icon name="ArrowRight" size={16} color="currentColor" />
                  </div>
                </div>
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-white rounded-2xl shadow-medium border border-border p-8 max-w-2xl mx-auto">
            <div className="flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mx-auto mb-4">
              <Icon name="Search" size={32} color="#8B4513" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-text-primary mb-3">
              Can't Find What You're Looking For?
            </h3>
            <p className="text-text-secondary font-body mb-6">
              Browse our complete directory of South Asian wedding vendors or use our AI-powered search to find exactly what you need.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="primary"
                size="lg"
                iconName="Grid3X3"
                iconSize={20}
                onClick={() => window.location.href = '/browse-categories-all-vendor-types'}
              >
                Browse All Categories
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Bot"
                iconSize={20}
                onClick={() => handleCategoryClick('ai-search')}
              >
                Try AI Search
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryShowcase;